// src/components/RunningTaskStrip.jsx
//
// "You are working on this, and here is how it is going against the estimate."
//
// Sits in the Development Tasks header. The page already knows which task is In
// Progress — the status dropdown refuses to start a second one — but it knew it
// silently: the only way to see how long you had been on something was to find
// the row and do the arithmetic against the Est. Time column yourself.
//
// The three facts are the ones somebody actually wants mid-task: how long so
// far, what it was supposed to take, and whether that line has been crossed.
// The bar carries the third without being read — it fills, changes colour near
// the end, and turns red past it — so a glance is enough and the numbers are
// there when a glance is not.
import React, { useEffect, useMemo, useState } from "react";

import api from "../utils/api";
import { apiPath } from "../utils/apiPath";
import { getCurrentUser } from "../utils/common";
import "../assets/css/runningTask.css";

/* Minutes to a compact human duration. Hours only appear once there is an
   hour, so a twenty-minute task is not padded out to "0h 20m". */
const dur = (mins) => {
  const m = Math.max(0, Math.round(mins));
  const h = Math.floor(m / 60);
  return h ? `${h}h ${String(m % 60).padStart(2, "0")}m` : `${m}m`;
};

const RunningTaskStrip = ({ task }) => {
  /* YOUR time on this task, not the task's.
   *
   * task.WorkedSeconds is the whole task's total — every stretch by every
   * person who has ever held it. On a task handed on halfway that is the
   * wrong number to put in front of the person now working it: pick up a task
   * somebody else spent three hours on and the strip opens at "3h 52m of 45m,
   * 3h 07m over" before you have done a minute of it.
   *
   * The per-person split already exists for the Task Details dialog
   * (sp_Queries_WorkedTime_ByUser). Asked here for the one task that is
   * running, which is cheap — one row, one task, only while something is
   * actually in progress — rather than adding a per-user column to the list
   * query, where a correlated pass over the tracker is what made that list
   * unusable once before.
   *
   *   undefined = not asked yet        -> show nothing rather than the wrong figure
   *   null      = asked and could not  -> fall back to the task total
   *   object    = this person's row
   */
  const [mine, setMine] = useState(undefined);
  const queryCode = task?.QueryCode ?? null;

  useEffect(() => {
    if (!queryCode) {
      setMine(undefined);
      return undefined;
    }

    let alive = true;
    setMine(undefined);

    api
      .get(`${apiPath.API_URL}${apiPath.tasks.workedTimeByUser}`, {
        params: { queryCode },
      })
      .then((res) => {
        if (!alive) return;
        const body = res?.data?.data ?? res?.data ?? [];
        const rows = Array.isArray(body) ? body : [];
        const meId = Number(getCurrentUser()?.userId) || null;
        setMine(rows.find((r) => Number(r.UserCode) === meId) ?? null);
      })
      .catch(() => {
        /* The task's own total is wrong for this readout, but it is a number
           and this is a glanceable strip — better than a permanent dash. */
        if (alive) setMine(null);
      });

    return () => {
      alive = false;
    };
  }, [queryCode]);
  /* A second, not a minute. The number is being watched — it is the reason
     somebody looks up here — and a display that jumps in whole minutes reads
     as broken while you are staring at it. */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!task) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [task]);

  const state = useMemo(() => {
    if (!task) return null;

    /* Time WORKED, not time since the task first existed.
     *
     * This counted from tbl_Queries.StartTime, which is stamped once — the very
     * first time a task is ever moved to In Progress — and never reset. So a
     * task started on Monday, held overnight and picked back up on Wednesday
     * opened at "49h 07m of 30m", which is the calendar telling you about two
     * nights and a hold rather than the work.
     *
     * The list now sends what the status tracker already knew: WorkedSeconds,
     * the In Progress / Testing In Progress stretches that have closed (holds
     * and overnight gaps excluded), and ActiveStartedAt, the moment the run
     * you are in began. Total = the first plus the second still ticking, which
     * is the figure the estimate was set against — and the same definition
     * sp_Queries_TotalTimeTaken banks into TimeTaken when the task finishes.
     *
     * Tasks that predate the tracker have neither, and fall back to the old
     * reading of StartTime: rough, but better than a blank where a number was. */
    /* mine wins where it is known. Its WorkedSeconds counts only the
       stretches this person started, and its ActiveStartedAt is when THEY
       resumed — which is also the only run the live seconds below belong to. */
    const workedMin =
      ((mine ? Number(mine.WorkedSeconds) : Number(task.WorkedSeconds)) || 0) /
      60;
    const startedFrom = mine?.ActiveStartedAt ?? task.ActiveStartedAt;
    const resumedAt = startedFrom ? new Date(startedFrom).getTime() : null;
    const tracked = resumedAt !== null || workedMin > 0;
    const legacyStart = task.StartTime ? new Date(task.StartTime).getTime() : null;

    const started = tracked ? resumedAt : legacyStart;
    const elapsedMin =
      (tracked ? workedMin : 0) +
      (started ? Math.max(0, (now - started) / 60000) : 0);
    const est = Number(task.EstimateTime) || 0;

    return {
      name: task.TaskName || task.NatureofQuery || `#${task.QueryNo}`,
      started,
      /* There is a figure to show as soon as any time has been worked, even in
         the moment between a status change and the refetch behind it, when the
         run has no start yet but the banked minutes are already real. */
      /* Nothing until the per-person figure has landed. A number that
         changes from 3h 52m to 12m a moment after it appears teaches people
         not to trust the strip; a brief dash teaches them nothing at all. */
      hasTime: mine !== undefined && (tracked || started !== null),
      /* Kept for the tooltip: the task has still had this much spent on it
         by everyone, and that should not vanish just because the headline is
         now personal. */
      taskTotalMin: (Number(task.WorkedSeconds) || 0) / 60,
      mineKnown: mine !== undefined && mine !== null,
      elapsedMin,
      est,
      /* No estimate means no judgement to make. The bar stays neutral rather
         than implying an overrun against a target nobody set. */
      pct: est > 0 ? Math.min(100, (elapsedMin / est) * 100) : null,
      over: est > 0 && elapsedMin > est,
      /* Amber from four fifths of the way through — early enough to wrap up,
         late enough not to nag. */
      near: est > 0 && elapsedMin >= est * 0.8 && elapsedMin <= est,
      remaining: est > 0 ? est - elapsedMin : null,
    };
  }, [task, now, mine]);

  if (!state) return null;

  const tone = state.over ? "is-over" : state.near ? "is-near" : "is-ok";

  return (
    /* A span, not a div: PageHeader renders the subtitle inside a
       <span>, and a block element in there is invalid nesting. The
       layout is inline-flex from the stylesheet either way. */
    <span
      className={`rts ${tone}`}
      /* The headline is this person's own time; the task's total across
         everyone who has held it is said here rather than dropped, so a task
         that is already over budget before you touched it is still knowable. */
      title={
        state.mineKnown && state.taskTotalMin > state.elapsedMin
          ? `Working on ${state.name} — your time on it. ` +
            `The task has had ${dur(state.taskTotalMin)} spent on it in total.`
          : `Working on ${state.name}`
      }
    >
      <span className="rts__dot" aria-hidden="true" />

      <span className="rts__name">{state.name}</span>

      {/* The elapsed figure is the headline — biggest, first, tabular so the
          digits do not shuffle sideways as they tick. */}
      <span className="rts__elapsed">
        {state.hasTime ? dur(state.elapsedMin) : "—"}
      </span>

      {/* Said out loud when this task has been worked by somebody else too.
          Without it "12m of 45m" reads as the whole story of the task, and
          the three hours already spent on it disappear. */}
      {state.mineKnown && state.taskTotalMin > state.elapsedMin && (
        <span className="rts__whose">yours</span>
      )}

      {state.est > 0 ? (
        <>
          <span className="rts__of">of {dur(state.est)}</span>

          <span className="rts__bar" aria-hidden="true">
            <span className="rts__fill" style={{ width: `${state.pct}%` }} />
          </span>

          {/* Says which side of the line this is on in words, because a colour
              alone is not an answer to "have I crossed it" — and is no answer
              at all to somebody who cannot separate the two colours. */}
          <span className="rts__verdict">
            {state.over
              ? `${dur(state.elapsedMin - state.est)} over`
              : `${dur(state.remaining)} left`}
          </span>
        </>
      ) : (
        <span className="rts__of rts__of--none">no estimate set</span>
      )}
    </span>
  );
};

export default RunningTaskStrip;
